import React, { useEffect, useRef, useState } from "react";
import TextField from "@material-ui/core/TextField";
import {
  parseLocaleNumber,
  formatLocaleNumber,
  isValidLocaleNumber,
} from "../heplers/localeFormat";
import { getTranslation } from "../heplers/translationHelper";

// Locale-aware, Formik-friendly numeric input.
//
// Contract:
//  - `value` is the canonical number (or numeric string) held in form state.
//  - While the field is focused the user types freely in their locale format.
//  - onChange emits the CANONICAL number: onChange({ target: { name, value } })
//    where value is a Number (or null when empty) -- never a separator-stripped
//    integer. This is what is sent to the API as numeric JSON.
//  - On blur the text is reformatted to grouped locale display, and malformed
//    input shows a visible "Invalid format" error instead of silently mangling.
const LocaleNumberField = ({
  value,
  onChange,
  onBlur,
  onFocus,
  name,
  label,
  error,
  helperText,
  disabled,
  required = true,
  size = "small",
  fullWidth = true,
  variant = "outlined",
  decimals = 2,
  ...rest
}) => {
  const [text, setText] = useState("");
  const [invalid, setInvalid] = useState(false);
  const focusedRef = useRef(false);

  // Reflect external value changes (load/reset) into the display, but never
  // clobber what the user is actively typing.
  useEffect(() => {
    if (focusedRef.current) return;
    if (value === "" || value == null) {
      setText("");
      setInvalid(false);
      return;
    }
    setText(formatLocaleNumber(value, { decimals }));
    setInvalid(false);
  }, [value, decimals]);

  const emit = (parsed) => {
    if (onChange) onChange({ target: { name, value: parsed } });
  };

  const handleChange = (e) => {
    const next = e.target.value;
    // Allow only digits, separators (. ,) and whitespace grouping while typing.
    if (!/^[0-9.,\s  ]*$/.test(next)) return;

    setText(next);

    if (next.trim() === "") {
      setInvalid(false);
      emit(null);
      return;
    }
    const ok = isValidLocaleNumber(next);
    setInvalid(!ok);
    emit(ok ? parseLocaleNumber(next) : null);
  };

  const handleFocus = (e) => {
    focusedRef.current = true;
    if (onFocus) onFocus(e);
  };

  const handleBlur = (e) => {
    focusedRef.current = false;
    if (text.trim() === "") {
      setInvalid(false);
    } else if (isValidLocaleNumber(text)) {
      setInvalid(false);
      setText(formatLocaleNumber(parseLocaleNumber(text), { decimals }));
    } else {
      setInvalid(true);
    }
    if (onBlur) onBlur(e);
  };

  const showError = Boolean(error) || invalid;

  return (
    <TextField
      {...rest}
      variant={variant}
      required={required}
      fullWidth={fullWidth}
      size={size}
      id={name}
      name={name}
      label={label}
      value={text}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      error={showError}
      helperText={invalid ? getTranslation("Invalid format") : helperText}
      placeholder={formatLocaleNumber(0, { decimals })}
      disabled={disabled}
      inputProps={{ inputMode: "decimal" }}
    />
  );
};

export default LocaleNumberField;
